import { DateTime } from "luxon";

import { GetRoleDetailsResponse, IGetRoleDetailsRequest } from "@/Administration/Api/Roles/Dto/GetRoleDetailsRequestResponse";
import { GetRoleHeadersResponse } from "@/Administration/Api/Roles/Dto/GetRoleHeadersResponse";
import { Client } from "@/Common/Api/Client";
import { GenericResponse } from "@/Common/Api/Dto/GenericResponse";
import { Guid } from "@/Common/Models/Guid";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { CreateRoleResponse, ICreateRoleRequest } from "./Dto/CreateRoleRequestResponse";
import { GetRoleExclusiveUsersResponse } from "./Dto/GetRoleExclusiveUsersResponse";
import { IUpdateRoleRequest } from "./Dto/UpdateRoleRequest";

class RolesApi {
    private static RoleHeadersCache: GetRoleHeadersResponse = null;
    private static RoleHeadersCacheExpiryTime: DateTime = null;
    public static async GetCachedRoleHeadersAsync(): Promise<GetRoleHeadersResponse> {
        if (!this.RoleHeadersCache || this.RoleHeadersCacheIsExpired) {
            this.SetRoleHeadersCache(await this.LoadRoleHeadersAsync());
        }

        return this.RoleHeadersCache;
    }

    public static async LoadRoleHeadersAsync(): Promise<GetRoleHeadersResponse> {
        return (await Client.postAsync<GetRoleHeadersResponse>(
            ServerOperations.Server.Myle_Administration_GetRoleHeaders,
            {}
        )).data;
    }

    public static async DeleteRole(roleId: Guid | string, userIdsToRemove?: string[]): Promise<GenericResponse> {
        this.ClearRoleHeadersCache();

        return (await Client.postAsync<GenericResponse>(
            ServerOperations.Server.Myle_Administration_DeleteRole,
            { roleId, userIdsToRemove }
        )).data;
    }

    public static async UpdateRole(request: IUpdateRoleRequest): Promise<GenericResponse> {
        this.ClearRoleHeadersCache();

        return (await Client.postAsync<GenericResponse>(
            ServerOperations.Server.Myle_Administration_UpdateRole,
            request
        )).data;
    }

    public static async CreateRole(request: ICreateRoleRequest): Promise<CreateRoleResponse> {
        this.ClearRoleHeadersCache();

        return (await Client.postAsync<CreateRoleResponse>(
            ServerOperations.Server.Myle_Administration_CreateRole,
            request
        )).data;
    }

    public static async GetRoleDetails(request: IGetRoleDetailsRequest): Promise<GetRoleDetailsResponse> {
        return (await Client.postAsync<GetRoleDetailsResponse>(
            ServerOperations.Server.Myle_Administration_GetRoleDetail,
            request
        )).data;
    }

    public static async GetRoleExclusiveUsers(roleId: Guid | string): Promise<GetRoleExclusiveUsersResponse> {
        return (await Client.postAsync<GetRoleExclusiveUsersResponse>(
            ServerOperations.Server.Myle_Administration_GetRoleExclusiveUsers,
            { roleId }
        )).data;
    }

    public static ClearRoleHeadersCache(): void {
        this.RoleHeadersCache = null;
        this.RoleHeadersCacheExpiryTime = null;
    }

    private static SetRoleHeadersCache(roleHeadersToCache: GetRoleHeadersResponse): void {
        this.RoleHeadersCache = roleHeadersToCache;
        this.RoleHeadersCacheExpiryTime = DateTime.now().plus({ minute: 5 });
    }

    private static get RoleHeadersCacheIsExpired(): boolean {
        if (!this.RoleHeadersCacheExpiryTime)
            return true;

        return DateTime.now() > this.RoleHeadersCacheExpiryTime;
    }
}

export { RolesApi };
